import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ProductService {
  id: string;
  company_id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  category_id: string | null;
  unit: string;
  default_cost_price: number;
  default_markup_pct: number;
  income_account: string | null;
  cogs_account: string | null;
  qb_item_id: string | null;
  qb_item_name: string | null;
  qb_tax_code: "TAX" | "NON";
  is_taxable: boolean;
  is_active: boolean;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_by: string | null;
  qb_synced: boolean;
  qb_synced_at: string | null;
  has_transactions: boolean;
  qty_mapping: any | null;
  created_at: string;
  updated_at: string;
}

export function useProductsServices(categoryFilter?: string, statusFilter?: string) {
  const { user } = useAuth();
  return useQuery<ProductService[]>({
    queryKey: ["products_services", categoryFilter, statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("products_services")
        .select("*")
        .order("code");
      if (categoryFilter && categoryFilter !== "all") {
        q = q.eq("category", categoryFilter);
      }
      if (statusFilter && statusFilter !== "all") {
        q = q.eq("status", statusFilter);
      } else {
        // By default exclude archived
        q = q.neq("status", "archived");
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ProductService[];
    },
    enabled: !!user,
  });
}

export function useCreateProductService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, any>) => {
      const { data, error } = await supabase
        .from("products_services")
        .insert(input as any)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products_services"] }),
  });
}

export function useUpdateProductService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & Record<string, any>) => {
      const { data, error } = await supabase
        .from("products_services")
        .update(input as any)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products_services"] }),
  });
}

export function useApproveProductService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("products_services")
        .update({
          status: "active",
          approved_by: (await supabase.auth.getUser()).data.user?.id,
          approved_at: new Date().toISOString(),
        } as any)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products_services"] }),
  });
}

export function useRejectProductService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data, error } = await supabase
        .from("products_services")
        .update({
          status: "inactive",
          rejected_by: (await supabase.auth.getUser()).data.user?.id,
          rejected_at: new Date().toISOString(),
          rejection_reason: reason,
        } as any)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products_services"] }),
  });
}

export function useDeactivateProductService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products_services")
        .update({ status: "inactive", is_active: false } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products_services"] }),
  });
}
